import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  calculateRetirement,
  type RetirementData,
  type RetirementResult,
} from "../store/retirementStore";

/** Adjustable slider parameters for the What-If panel */
export interface WhatIfSliderValues {
  retirementAge: number;
  lifeExpectancy: number;
  expectedReturnRate: number;
  inflationRate: number;
  /** Decimal change in expenses at retirement (e.g., -0.20 = 20% reduction) */
  expensesAtRetirement: number;
}

export type WhatIfSliderKey = keyof WhatIfSliderValues;

/** Debounce delay for scenario recalculation (ms) */
const DEBOUNCE_MS = 500;

interface UseWhatIfScenarioReturn {
  /** Whether scenario mode is active */
  isActive: boolean;
  /** Whether a debounced recalculation is pending */
  isRecalculating: boolean;
  /** Current slider values (null until baseData is available) */
  sliderValues: WhatIfSliderValues | null;
  /** Scenario calculation result (null when inactive) */
  scenarioResult: RetirementResult | null;
  /** Scenario retirement data (null when inactive) */
  scenarioData: RetirementData | null;
  /** Update a single slider value — activates scenario mode */
  setSliderValue: (key: WhatIfSliderKey, value: number) => void;
  /** Reset all sliders to saved values and exit scenario mode */
  reset: () => void;
}

function extractSliderValues(data: RetirementData): WhatIfSliderValues {
  return {
    retirementAge: data.retirementAge,
    lifeExpectancy: data.lifeExpectancy,
    expectedReturnRate: data.annualReturn,
    inflationRate: data.inflationRate,
    expensesAtRetirement: data.expensesAtRetirement,
  };
}

function applyOverrides(
  data: RetirementData,
  sliders: WhatIfSliderValues
): RetirementData {
  return {
    ...data,
    retirementAge: sliders.retirementAge,
    lifeExpectancy: sliders.lifeExpectancy,
    annualReturn: sliders.expectedReturnRate,
    inflationRate: sliders.inflationRate,
    expensesAtRetirement: sliders.expensesAtRetirement,
  };
}

const EMPTY_OVERRIDES: Partial<WhatIfSliderValues> = {};

/**
 * Hook for managing What-If scenario mode.
 *
 * When a slider value changes, scenario mode activates and projections are
 * recalculated after a debounce delay. Changes are local-only — nothing
 * is persisted to Firestore until the user explicitly saves.
 */
export function useWhatIfScenario(
  baseData: RetirementData | null
): UseWhatIfScenarioReturn {
  const [isActive, setIsActive] = useState(false);
  const [overrides, setOverrides] =
    useState<Partial<WhatIfSliderValues>>(EMPTY_OVERRIDES);
  const [debouncedOverrides, setDebouncedOverrides] =
    useState<Partial<WhatIfSliderValues>>(EMPTY_OVERRIDES);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Detect external baseData changes and reset overrides.
  // Uses React's recommended "setState during render" pattern to synchronize
  // derived state with changing props (avoids useEffect + setState cascade).
  const [prevBaseData, setPrevBaseData] = useState(baseData);
  if (baseData !== prevBaseData) {
    setPrevBaseData(baseData);
    if (isActive) {
      setOverrides(EMPTY_OVERRIDES);
      setDebouncedOverrides(EMPTY_OVERRIDES);
      setIsActive(false);
    }
  }

  const baseSliders = useMemo<WhatIfSliderValues | null>(
    () => (baseData ? extractSliderValues(baseData) : null),
    [baseData]
  );

  const sliderValues: WhatIfSliderValues | null = useMemo(
    () => (baseSliders ? { ...baseSliders, ...overrides } : null),
    [baseSliders, overrides]
  );

  const setSliderValue = useCallback((key: WhatIfSliderKey, value: number) => {
    setIsActive(true);
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Debounce override changes — only the timer callback calls setState,
  // avoiding synchronous setState inside the effect body.
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedOverrides(overrides);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [overrides]);

  const debouncedSliders: WhatIfSliderValues | null = useMemo(
    () => (baseSliders ? { ...baseSliders, ...debouncedOverrides } : null),
    [baseSliders, debouncedOverrides]
  );

  // Compute scenario from debounced slider values (synchronous, no effect needed)
  const { scenarioData, scenarioResult } = useMemo<{
    scenarioData: RetirementData | null;
    scenarioResult: RetirementResult | null;
  }>(() => {
    if (!isActive || !baseData || !debouncedSliders)
      return { scenarioData: null, scenarioResult: null };
    const sd = applyOverrides(baseData, debouncedSliders);
    return { scenarioData: sd, scenarioResult: calculateRetirement(sd) };
  }, [isActive, baseData, debouncedSliders]);

  // Derived: true while slider values are ahead of the debounced computation
  const isRecalculating = isActive && overrides !== debouncedOverrides;

  const reset = useCallback(() => {
    setIsActive(false);
    setOverrides(EMPTY_OVERRIDES);
    setDebouncedOverrides(EMPTY_OVERRIDES);
  }, []);

  return {
    isActive,
    isRecalculating,
    sliderValues,
    scenarioResult,
    scenarioData,
    setSliderValue,
    reset,
  };
}
